import {
  Component, OnInit, ChangeDetectionStrategy,
  OnDestroy, Input
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { HotelDetailResponse, RefundableType, BreakfastNotIncluded } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { StyleDots } from '@app/modules/carousel/entities/carousel-direction';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import {
  HotelSearchedRoomRate, HotelSearchedRoom, HotelRoomImages
} from '@hotels/hotel-results/entities/hotel-searched';
import { TooltipOrientation } from '@shared/tooltip/entities/tooltip-orientation';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { HotelDetailActionService } from '@hotels/hotel-detail/services/hotel-detail-action.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { ProductTags } from '@app/constants/product-tags.constants';
import { RoomEvent } from '@hotels/hotel-detail/components/hotel-room-price/hotel-room-price.component';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-hotel-rooms-mobile',
  templateUrl: './hotel-rooms-mobile.component.html',
  styleUrls: ['./hotel-rooms-mobile.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelRoomsMobileComponent implements OnInit, OnDestroy {

  @Input() hasRappiCard: boolean;
  @Input() isPrime: boolean;
  @Input() isLoggedIn: boolean;
  @Input() classTag: ProductTags;
  @Input() isByhours: boolean;
  @Input() hourType: number;
  @Input() checkInTimes: string[];

  public styleDots: StyleDots = StyleDots.HotelsResults;
  public typeImageHotel: string = IMAGE_TYPE.HOTEL;
  public hotelDetail: HotelDetailResponse | null;
  public oneDay: number = (1000 * 60 * 60 * 24);
  public showTooltip: boolean;
  public tooltipOrientation: string = TooltipOrientation.RIGHT;
  public amenitiesBasePath: string = 'web/travel/amenities';
  public noRoomImages: string = 'placeholder-hab.svg';
  public hasImage = true;
  public showHoursSwipe = false;
  public loadingRoom = false;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  private subscriptions: Subscription[] = [];
  private childrenSingle: string = this.getTranslate('BOOKING_MODULE.ITINERARY_PASSENGERS.SINGLE.CHILDREN') as string;
  private childrenMultiple: string = this.getTranslate('BOOKING_MODULE.ITINERARY_PASSENGERS.MULTIPLE.CHILDREN') as string;
  private adultsSingle: string = this.getTranslate('BOOKING_MODULE.ITINERARY_PASSENGERS.SINGLE.ADULTS') as string;
  private adultsMultiple: string = this.getTranslate('BOOKING_MODULE.ITINERARY_PASSENGERS.MULTIPLE.ADULTS') as string;

  constructor(
    private facade: HotelDetailFacade,
    private route: ActivatedRoute,
    private translate: TranslocoService,
    private domManipulateService: DomManipulateService,
    private hotelDetailActionService: HotelDetailActionService,
    private baseSearchBarFacade: BaseSearchBarFacade,
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.hotelDetail$.subscribe(hotelDetail => this.hotelDetail = hotelDetail)
    );
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get hotelDetail$(): Observable<HotelDetailResponse> | null {
    return this.facade.hotelDetail$.pipe(map(detail => detail.Info || detail.Result ? detail : null));
  }

  get isPackageDetail$(): Observable<boolean> {
    return this.baseSearchBarFacade.packageStep$.pipe(map(packageStep =>
      !!packageStep && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL));
  }

  get params(): Params {
    return this.route.snapshot.params;
  }

  public getTranslate(key: string | string[]): string | string[] {
    return this.translate.translate(key);
  }

  get nights(): number {
    const checkOutDate = this.hotelDetail &&
      this.hotelDetail.Result ? new Date(this.hotelDetail.Result.CheckoutDate).getTime() : 0;
    const checkInDate = this.hotelDetail &&
      this.hotelDetail.Result ? new Date(this.hotelDetail.Result.CheckinDate).getTime() : 0;
    const diffTime = Math.abs(checkOutDate - checkInDate);
    return Math.ceil(diffTime / this.oneDay);
  }

  get isGroupedView(): boolean {
    return !!this.hotelDetail?.Result?.RoomsByCode?.length;
  }

  public roomByRoomId(roomId: number): HotelSearchedRoom {
    return this.hotelDetail.Result.Rooms.find(x => x.RoomId === roomId);
  }

  public breakfastIncluded(boardCode: string): boolean {
    return !BreakfastNotIncluded.includes(boardCode);
  }

  public isRefundable(refundableType: string): boolean {
    return refundableType !== RefundableType.NonRefundable;
  }

  public selectRoom(roomData: RoomEvent, roomCode: string): void {
    this.loadingRoom = true;
    this.hotelDetailActionService.selectHotelRoom(
      roomData.room, 
      this.route.queryParamMap, 
      roomCode, 
      roomData
    );
  }

  public toggleTooltip(value: boolean): void {
    this.showTooltip = value;
  }

  public roomHeaderLabel(room: HotelSearchedRoom): string {
    if (isNullOrUndefined(room)) {
      return '';
    }
    const childrenLabel = room.Children > 1 ? this.childrenMultiple : this.childrenSingle;
    const adultsLabel = room.Adults > 1 ? this.adultsMultiple : this.adultsSingle;
    const guestLabel = room.Children === 0 ? `${room.Adults} ${adultsLabel}` :
      `${room.Adults} ${adultsLabel}, ${room.Children} ${childrenLabel}`;
    return guestLabel;
  }

  public roomImage(roomImages: HotelRoomImages[]): string {
    if (isNullOrUndefined(roomImages) || roomImages.length === 0) {
      return '';
    }
    return roomImages[0].Url;
  }

  public numberRooms(roomRate: HotelSearchedRoomRate): number {
    return roomRate && roomRate.RoomIds ? roomRate.RoomIds.length : 0;
  }

  public trackByIdx(index: number, roomRate: HotelSearchedRoom): string {
    return roomRate?.RoomRateById?.RateId || `${index}`;
  }

  public errorImage(event: boolean) {
    this.hasImage = !event;
  }

}
